import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { SubmitHandler } from 'react-hook-form'
import {
    CreateTabelaHorariosInput,
    CreateTabelaHorariosSchema,
    funcionario,
    tabelaHorarios,
    trocaHorario,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { updateRascunho } from 'templates/Cadastro/Funcionarios/organismos/modals/Adicionar'
import { z } from 'zod'
import { diasTabelaHorario } from '../constants/dias-tabela-horario'
import { CanSubmitProvider, useCanSubmit } from '../context/CanSubmitContext'
import FormAssociarFuncionarios from '../forms/FormAssociarFuncionarios'
import FormTabelaHorario from '../forms/FormTabelaHorario'
import {
    AssociarFuncionarios,
    AssociarFuncionariosSchema,
} from '../schemas/associar-funcionarios.schema'

type FormValues = CreateTabelaHorariosInput & AssociarFuncionarios

function AdicionarTabelaHorariosContent() {
    const empresaId = useStore(state => state.companyId)
    const { mutateAsync: createTabela, isLoading: creatingTabela } =
        tabelaHorarios.mutations.useCreate({ invalidateQuery: true })
    const { mutateAsync: createTroca, isLoading: creatingTroca } =
        trocaHorario.mutations.useCreate({ invalidateQuery: true })
    const { canSubmit } = useCanSubmit()
    const canAssociarFuncionarios = hasPermission([
        'create',
        'TrocaDeTabelaDeHorário',
    ])

    const handleSubmit: SubmitHandler<FormValues> = async data => {
        const { funcionarios, ...tabelaData } = data

        let tabela: { id: number }
        try {
            tabela = await createTabela(tabelaData)
        } catch (err: any) {
            toast.error(err.message)
            throw err
        }

        updateRascunho({ tabelaHorarioId: tabela.id })

        if (!funcionarios?.funcionarioIds?.length) {
            toast.success('Tabela adicionada com sucesso')
            return
        }

        try {
            await createTroca({
                empresaId,
                tabelaHorarioId: tabela.id,
                funcionarioIds: funcionarios.funcionarioIds,
                dtInicial: funcionarios.dtInicial,
                dtFinal: funcionarios.dtFinal,
            } as any)
            await funcionario.invalidateQueries()
            toast.success(
                'Tabela adicionada e funcionários vinculados com sucesso',
            )
        } catch (err: any) {
            toast.error(
                `Tabela salva, mas houve erro ao vincular os funcionários: ${err.message}`,
            )
        }
    }

    return (
        <TabbedDialog.Root
            height="auto"
            width="70rem"
            loading={creatingTabela || creatingTroca}
            onSubmit={handleSubmit}
            schema={z.intersection(
                CreateTabelaHorariosSchema,
                AssociarFuncionariosSchema,
            )}
            defaultValues={{
                tipo: 'DIARIA',
                horarios: diasTabelaHorario.map(it => ({
                    label: it,
                    Tot: '00:00',
                })),
                tolerancias: {
                    entradaAntes: 0,
                    entradaDepois: 0,
                    saidaAntes: 0,
                    saidaDepois: 0,
                    toleranciaTurno: 0,
                    toleranciaDia: 0,
                },
                tempoPausas: '00:00',
                totSem: '00:00',
                diaDescontoDSR: '-2',
                toleranciaSemana: 0,
                limiteDiario: '',
                intervaloDesconto: '',
                intervaloAposHoras: '',
                funcionarios: { funcionarioIds: [] },
            }}
            open={hasPermission(['create', 'TabelaDeHorários'])}
            tabs={[
                {
                    name: 'tabela',
                    label: 'Tabela de horários',
                    content: <FormTabelaHorario />,
                },
                ...(canAssociarFuncionarios
                    ? [
                          {
                              name: 'funcionarios',
                              label: 'Funcionários',
                              content: <FormAssociarFuncionarios />,
                          },
                      ]
                    : []),
            ]}
        >
            <TabbedDialog.Title>
                Adicionar tabela de horários
            </TabbedDialog.Title>
            <TabbedDialog.Content dividers />
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
                <TabbedDialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!canSubmit}
                />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}

export default function AdicionarTabelaHorarios() {
    return (
        <CanSubmitProvider>
            <AdicionarTabelaHorariosContent />
        </CanSubmitProvider>
    )
}
